import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, UserRound } from 'lucide-react';

import { PageHeader } from '@/components/PageHeader';
import { ProfileChangePasswordTab } from '@/components/profile/ProfileChangePasswordTab';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ROLE_LABEL } from '@/constants/roles';
import { LoadingBlock } from '@/components/ui/Loader';
import { QueryError } from '@/components/ui/QueryError';
import { authApi } from '@/features/auth/api/auth.api';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { StaffProfilePage } from '@/features/institute/staff/components/StaffProfilePage';
import { staffApi } from '@/features/institute/staff/api/staff.api';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useLiveFormErrors } from '@/hooks/useLiveFormErrors';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { applyFieldErrors } from '@/lib/forms';
import { cn } from '@/lib/utils';
import { CONSOLE_PALETTE } from '../../theme';
import { myAccountSchema, type MyAccountFormValues } from '../schemas/myAccount.schema';

/**
 * The administrator's own profile — the same screen the Staff directory opens
 * from the eye on their row, so the two never drift apart.
 *
 * Not the institute's profile (`/admin/institute`), which is the school's. The
 * record is read by the signed-in account's uuid: a staff row and its account
 * share one. Editing goes to the account form below rather than the office's
 * staff editor, because the server refuses that editor for the owner account —
 * their name, sign-in details and password are saved through `PATCH /auth/me`.
 */
export function AdminProfilePage() {
  const user = useAuthStore((state) => state.user);
  const permissions = user?.permissions;
  const canManage = Boolean(permissions?.includes('STAFF_MANAGE'));
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);

  const { data: member, isLoading, error, refetch } = useApiQuery(
    () => (user ? staffApi.get(user.uuid) : Promise.resolve(null)),
    [user?.uuid],
  );

  if (isEditing) {
    return (
      <AccountEditor
        onDone={() => {
          setIsEditing(false);
          // The name, email or mobile may have changed under the profile.
          refetch();
        }}
      />
    );
  }

  if (isLoading) return <LoadingBlock label="Loading your profile…" className="py-20" />;

  if (error || !member) {
    return (
      <div className={cn('flex flex-col gap-6', CONSOLE_PALETTE)}>
        <PageHeader title="My profile" />
        <QueryError
          title="Could not load your profile"
          error={error ?? { message: 'Your staff record could not be found. Please contact support.' }}
          onRetry={refetch}
        />
      </div>
    );
  }

  return (
    <StaffProfilePage
      member={member}
      onBack={() => navigate('/admin')}
      backLabel="Dashboard"
      canManage={canManage}
      onChanged={() => refetch()}
      onEdit={() => setIsEditing(true)}
    />
  );
}

/**
 * The administrator's account — their name, sign-in details and password. An
 * administrator has no staff record for the office to keep, so their account is
 * the whole of what they edit. The password card is the one every other
 * panel's profile uses.
 */
function AccountEditor({ onDone }: { onDone: () => void }) {
  const user = useAuthStore((state) => state.user);
  const setUser = useAuthStore((state) => state.setUser);
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm<MyAccountFormValues>({
    resolver: zodResolver(myAccountSchema),
    defaultValues: { firstName: '', lastName: '', email: '', mobile: '', currentPassword: '' },
  });
  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isSubmitting, isDirty },
  } = form;
  useLiveFormErrors(form);

  // Filled from the signed-in account, and again after each save.
  useEffect(() => {
    if (!user) return;
    reset({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      mobile: user.phone ?? '',
      currentPassword: '',
    });
  }, [user, reset]);

  const email = watch('email');
  const mobile = watch('mobile');
  // A new email or mobile is a new way to sign in, so the server asks for the
  // password first — and the field is only shown when it will be asked for.
  const signInChanged =
    Boolean(user) &&
    (email.trim().toLowerCase() !== user?.email || mobile.replace(/[\s-]/g, '') !== (user?.phone ?? ''));

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    const parsed = myAccountSchema.parse(values);
    try {
      const { user: updated } = await authApi.updateMyAccount({
        firstName: parsed.firstName,
        lastName: parsed.lastName,
        email: parsed.email,
        mobile: parsed.mobile,
        ...(signInChanged && { currentPassword: parsed.currentPassword }),
      });
      setUser(updated);
      toast.success('Profile saved', 'Your details have been updated.');
    } catch (error) {
      if (error instanceof ApiRequestError) {
        applyFieldErrors(setError, error);
        setFormError(error.message);
        return;
      }
      setFormError('Could not save your profile. Please try again.');
    }
  });

  return (
    <div className={cn('flex flex-col gap-6', CONSOLE_PALETTE)}>
      <div>
        <Button variant="secondary" size="sm" leftIcon={<ArrowLeft className="h-4 w-4" aria-hidden />} onClick={onDone}>
          Back to my profile
        </Button>
      </div>
      <PageHeader
        title="Edit my profile"
        description="Your own account at your school — the name colleagues see, and how you sign in."
      />

      <section className="card flex flex-col gap-5 p-5 sm:p-7" aria-labelledby="account-details-heading">
        <div className="flex items-center gap-3.5 border-b border-line pb-4">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-line bg-surface-2 text-accent">
            <UserRound className="h-5 w-5" aria-hidden />
          </span>
          <div>
            <h2 id="account-details-heading" className="text-base sm:text-lg font-bold text-ink">
              Account details
            </h2>
            <p className="text-sm text-ink-3">{user?.instituteRole ?? (user ? ROLE_LABEL[user.role] : '')}</p>
          </div>
        </div>

        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
          {formError && (
            <Alert tone="critical" title="Could not save your profile">
              {formError}
            </Alert>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Input label="First name" autoComplete="given-name" required error={errors.firstName?.message} {...register('firstName')} />
            <Input label="Last name" autoComplete="family-name" required error={errors.lastName?.message} {...register('lastName')} />
            <Input
              label="Email"
              type="email"
              autoComplete="email"
              required
              hint="You sign in with this address."
              error={errors.email?.message}
              {...register('email')}
            />
            <Input
              label="Mobile number (optional)"
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              hint="You can also sign in with this number."
              error={errors.mobile?.message}
              {...register('mobile')}
            />
          </div>

          {signInChanged && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="Current password"
                type="password"
                autoComplete="current-password"
                required
                hint="Needed to change your email or mobile number."
                error={errors.currentPassword?.message}
                {...register('currentPassword')}
              />
            </div>
          )}

          <div className="flex justify-end">
            <Button type="submit" isLoading={isSubmitting} disabled={!isDirty}>
              Save changes
            </Button>
          </div>
        </form>
      </section>

      <ProfileChangePasswordTab />
    </div>
  );
}
